import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiBriefcase, FiUsers, FiHeart, FiAward } from 'react-icons/fi';
import { userApi } from '../../api/userApi';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const perks = [
  {
    icon: <FiHeart className="w-6 h-6" />,
    title: 'Health & Wellness',
    description: 'Comprehensive health insurance and wellness programs for you and your family'
  },
  {
    icon: <FiAward className="w-6 h-6" />,
    title: 'Growth & Development',
    description: 'Regular training sessions and career advancement opportunities'
  },
  {
    icon: <FiUsers className="w-6 h-6" />,
    title: 'Collaborative Culture',
    description: 'Work with passionate individuals in a creative environment'
  }
];

const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setIsLoading(true);
        const data = await userApi.getJobs();
        setJobs(data);
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
        setError('Failed to load job listings');
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobs();
  }, []);

  return (
    <>
      <Helmet>
        <title>Careers | Panchali Boutique</title>
        <meta 
          name="description" 
          content="Join our team at Panchali Boutique and be part of creating exceptional ethnic wear experiences" 
        />
      </Helmet>

      <div className="flex flex-col min-h-screen">
        <Navbar />
        
        <main className="flex-grow">

          {/* Values Section */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-3xl font-light mb-4">Why Join Us</h2>
                <div className="w-24 h-[2px] bg-gradient-to-r from-[#2C3E50] to-transparent mx-auto"></div>
              </motion.div>

              <div className="grid md:grid-cols-3 gap-8">
                {perks.map((perk, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center p-6"
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#2C3E50]/10 mb-4">
                      {perk.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{perk.title}</h3>
                    <p className="text-gray-600">{perk.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Open Positions */}
          <section className="py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-3xl font-light mb-4">Open Positions</h2>
                <div className="w-24 h-[2px] bg-gradient-to-r from-[#2C3E50] to-transparent mx-auto"></div>
              </motion.div>

              {isLoading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2C3E50]"></div>
                </div>
              ) : error ? (
                <div className="text-center text-red-600 py-10">
                  <p>{error}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {jobs.map((job) => (
                    <motion.div
                      key={job.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="bg-white p-8 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <h3 className="text-xl font-semibold mb-2">{job.title}</h3>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
                            <span className="flex items-center">
                              <FiBriefcase className="mr-2" />
                              {job.department_display}
                            </span>
                            <span>•</span>
                            <span>{job.location}</span>
                            <span>•</span>
                            <span>{job.employment_type_display}</span>
                          </div>
                          <p className="text-gray-600 mb-4">{job.description}</p>
                          <div className="space-y-2">
                            {job.requirements.split('\n').map((req, index) => (
                              <div key={index} className="flex items-center text-sm text-gray-600">
                                <span className="w-1.5 h-1.5 bg-[#2C3E50] rounded-full mr-2"></span>
                                {req.replace('-', '').trim()}
                              </div>
                            ))}
                          </div>
                        </div>
                        <button className="px-6 py-3 bg-[#2C3E50] text-white hover:bg-[#34495E] transition-colors">
                          Apply Now
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Contact Section */}
          <section className="py-20 bg-[#2C3E50] text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-3xl font-light mb-4">Don't see the right role?</h2>
              <p className="text-lg mb-8">
                We're always looking for talented individuals to join our team.
                Send us your resume and we'll keep you in mind for future opportunities.
              </p>
              <button className="px-8 py-3 bg-white text-[#2C3E50] hover:bg-gray-100 transition-colors">
                Send Resume
              </button>
            </div>
          </section>
        </main>

      </div>
    </>
  );
};

export default CareersPage;








